import { $sceneMap, $tourDataApi } from '@stores/dataStores';
import {
  $editingImageMarker,
  $editingImageMarkerApi,
} from '@stores/editingMarkerStores';
import { $playerWidgetsApi } from '@stores/playerWidgets';
import { type GeneralID, type STImageMarker, STImageMarkerType } from '@types';
import { useStore } from 'effector-react';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import useConstant from 'use-constant';
import PanelBase from '../../panel/PanelBase';
import { stEditorKrpPlayer } from '../STKrpPlayer';
import { showBatchAddTip } from '../components/BatchAddTip';
import { MarkerSettingsScene } from './MarkerSettingsScene';

interface Props {
  sandTableId: string;
}

export function PanelSandTableImage({ sandTableId }: Props) {
  const {
    marker,
    isNew,
    zOrder = 0,
    batchMode = false,
    isActive,
  } = useStore($editingImageMarker)!;
  const { id, icon, data, showTitle, title } = marker;
  const sceneMap = useStore($sceneMap);
  const firstState = useConstant(() => marker);
  const [idList, setIdList] = useState<GeneralID[]>([]);

  useEffect(() => {
    return () => {
      $editingImageMarkerApi.clear();
      $playerWidgetsApi.clear();
    };
  }, []);

  useEffect(() => {
    if (!batchMode) {
      stEditorKrpPlayer.startEditing(marker);
      return () => {
        stEditorKrpPlayer.quitEditing();
      };
    }
  }, [batchMode]);

  const handleClose = () => {
    $editingImageMarkerApi.clear();
  };

  const handleDelete = () => {
    if (!isNew) {
      $tourDataApi.updateSandtable({
        id: sandTableId,
        updater: (draft) => {
          const list = draft.markers as STImageMarker[];
          draft.markers = list.filter((x) => x.id !== id);
        },
      });
    }
    stEditorKrpPlayer.removeMarker(id);
  };

  const handleConfirm = () => {
    if (batchMode) {
      if (!idList.length) return;
      const { atv, ath } = stEditorKrpPlayer.getCenter();
      const newMarkers: STImageMarker[] = idList.map((_id) => {
        const sceneData = sceneMap.get(_id)!;
        return {
          ...marker,
          id: `im_${nanoid()}`,
          title: sceneData.name,
          icon: { ...marker.icon, atv, ath },
          data: {
            type: 0,
            sceneType: sceneData.type,
            sceneId: sceneData.id,
          },
        };
      });
      $tourDataApi.updateSandtable({
        id: sandTableId,
        updater: (draft) => {
          const list = draft.markers as STImageMarker[];
          draft.markers = [...list, ...newMarkers];
        },
      });
      newMarkers.forEach((x, i) => {
        stEditorKrpPlayer.addMarker(x, {
          dragable: true,
          zOrder: zOrder + i,
        });
      });
      showBatchAddTip();
    } else {
      $tourDataApi.updateSandtable({
        id: sandTableId,
        updater: (draft) => {
          const list = draft.markers as STImageMarker[];
          const index = list.findIndex((x) => x.id === id);
          if (index > -1) {
            list[index] = marker;
          } else {
            list.push(marker);
          }
        },
      });
      stEditorKrpPlayer.addMarker(marker, { zOrder, dragable: true });
    }
  };

  const renderContent = () => {
    if (marker.type === undefined || marker.type === STImageMarkerType.scene) {
      return (
        <MarkerSettingsScene
          marker={marker}
          batchMode={batchMode}
          isActive={isActive}
          idList={idList}
          onIdListChange={setIdList}
        />
      );
    }
  };

  return (
    <PanelBase
      title="添加标记"
      isChanged={() => firstState !== marker}
      onConfirm={handleConfirm}
      onRequestClose={handleClose}
      onDeleteClick={batchMode ? undefined : handleDelete}
    >
      {renderContent()}
    </PanelBase>
  );
}
